import lockBody from "@Utils/lockBody";
import unlockBody from "@Utils/unLockBody";
import { useEffect } from "react";
import appStyles from "@App/app.module.scss";
import { isViewTransitionNavigating } from "@Utils/viewTransitionNavUtility";

const addModalHeight = () => {
  document?.body?.classList?.add(appStyles.addFullHeight);
  const html = document?.getElementsByTagName("html");
  const reactView = document.getElementById("react-view");
  const app = document.getElementById("app");
  reactView?.classList.add(appStyles.addFullHeight);
  app?.classList.add(appStyles.addFullHeight);
  html?.[0]?.classList.add(appStyles.addFullHeight);
};

const removeModalHeight = () => {
  document?.body?.classList?.remove(appStyles.addFullHeight);
  const html = document?.getElementsByTagName("html");
  const reactView = document.getElementById("react-view");
  const app = document.getElementById("app");
  reactView?.classList.remove(appStyles.addFullHeight);
  app?.classList.remove(appStyles.addFullHeight);
  html?.[0]?.classList.remove(appStyles.addFullHeight);
};

export const unwrapState = (state: any): Record<string, any> => {
  if (state && typeof state === "object") {
    // Some routers/history-libs wrap state: { key, state: actualState }
    // We unwrap recursively to find the level that contains our _modals stack.
    if (state.state && typeof state.state === "object") {
      return unwrapState(state.state);
    }
    return state;
  }
  return {};
};

const getModalState = (history: any): Record<string, any> => {
  const routerState = history?.location?.state;
  if (routerState) {
    return unwrapState(routerState);
  }

  if (typeof window !== "undefined" && window.history?.state) {
    return unwrapState(window.history.state);
  }

  return {};
};

export const getModalsFromState = (state: Record<string, any>): string[] => {
  if (Array.isArray(state._modals)) {
    return state._modals;
  }
  if (state._modal) {
    return [state._modal];
  }
  return [];
};

const useModal = (
  history: any,
  location: any,
  modalType: string,
  handleOnBackClick: (e: any) => void,
  queryParams = "",
  addFullHeight = false,
  // Optional guard shared with the caller (e.g. BottomSheetOverlay) so that a
  // goBack() already fired by the caller's own close path is not fired a second
  // time by this hook's unmount cleanup. Without this, closing a sheet while its
  // caller's 300ms goBack timer is pending pops two history entries instead of
  // one, navigating the user away from the current page.
  didGoBackRef?: { current: boolean },
  // When false the hook is a no-op. Passed as an argument (instead of the caller
  // conditionally calling the hook) so the hook is always invoked in the same
  // order every render, satisfying the rules of hooks.
  enabled = true,
) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // ---------------------------------------------------------------------------
    // 1. Read the current modal stack from history state.
    // ---------------------------------------------------------------------------
    const existingState = getModalState(history);
    const prevModals = getModalsFromState(existingState);

    const isAlreadyInStack = prevModals.includes(modalType);
    const myIndex = isAlreadyInStack ? prevModals.lastIndexOf(modalType) : prevModals.length;

    // Track whether we pushed a history entry and whether it was already
    // popped via back-navigation. If the component unmounts without the
    // entry being popped (e.g. close button), we clean it up ourselves.
    let didPush = false;
    let wasPopped = false;

    // ---------------------------------------------------------------------------
    // 2. popstate handler — fires on browser Back / Forward.
    // ---------------------------------------------------------------------------
    const handleOnHistoryPop = () => {
      if (typeof window !== "undefined") {
        const currentState = window.history?.state ? unwrapState(window.history.state) : {};
        const currentModals = getModalsFromState(currentState);

        if (currentModals[myIndex] !== modalType) {
          wasPopped = true;
          // Our pushed entry has already been popped by this back-navigation,
          // so the unmount cleanup below must not attempt another goBack().
          if (didGoBackRef) {
            didGoBackRef.current = true;
          }
          handleOnBackClick(null);
        }
      }
    };

    // ---------------------------------------------------------------------------
    // 3. Body scroll lock.
    // ---------------------------------------------------------------------------
    lockBody();

    if (addFullHeight) {
      addModalHeight();
    }

    // ---------------------------------------------------------------------------
    // 4. Push a new history entry so that Back closes this modal.
    //    Skip the push if the modal is already in the stack (reload / forward).
    // ---------------------------------------------------------------------------
    if (!isAlreadyInStack) {
      const newModals = [...prevModals, modalType];

      // Merge filter state from window.history.state (set by applyFilter via
      // window.history.pushState, which bypasses connected-react-router) so
      // that the modal's history entry preserves filter query params when the
      // user navigates back.
      const windowState =
        typeof window !== "undefined" && window.history?.state
          ? unwrapState(window.history.state)
          : {};
      const modalState = {
        ...windowState,
        ...existingState,
        _modal: modalType,
        _modals: newModals,
      };

      // When query params or filter state already exist we avoid changing the
      // visible URL (keeps the filter UX intact on mobile). We still push a
      // history entry so that Back navigation works.
      const currentLocationSearch =
        typeof window !== "undefined" ? window.location.search : location.search;
      const searchParams = new URLSearchParams(currentLocationSearch || "");
      const hasAnyQueryParams = Array.from(searchParams.keys()).length > 0;
      const historyFilters =
        (typeof window !== "undefined" && window.history?.state?.filters) ||
        existingState.filters ||
        null;
      const shouldSkipUrlUpdate = Boolean(hasAnyQueryParams || historyFilters);

      // Use window.location instead of the router's location object because
      // applyFilter updates the URL via window.history.pushState directly
      // (bypassing connected-react-router), so the router's location can be
      // stale — missing filter query params and even having the wrong pathname
      // (e.g. after a university/locality filter changes the path).
      const currentPathname =
        typeof window !== "undefined" ? window.location.pathname : location.pathname;
      const currentSearch =
        typeof window !== "undefined" ? window.location.search : location.search;

      if (shouldSkipUrlUpdate) {
        history.push({
          pathname: currentPathname,
          search: currentSearch,
          state: modalState,
        });
      } else if (queryParams && !currentSearch?.includes(queryParams)) {
        // Append the modal-specific query params to the URL.
        const separator = currentSearch ? "&" : "?";
        const url = `${currentPathname}${currentSearch || ""}${separator}${queryParams}`;
        history.push(url, modalState);
      } else {
        // No query params to add — push with the current path.
        const url = `${currentPathname}${currentSearch || ""}`;
        history.push(url, modalState);
      }

      didPush = true;
    }

    // ---------------------------------------------------------------------------
    // 5. Listen for popstate.
    // ---------------------------------------------------------------------------
    window.addEventListener("popstate", handleOnHistoryPop);

    return () => {
      unlockBody();
      if (addFullHeight) {
        removeModalHeight();
      }
      window.removeEventListener("popstate", handleOnHistoryPop);

      // -----------------------------------------------------------------------
      // 6. Clean up ghost history entry on unmount.
      //    If we pushed a history entry and the modal is being unmounted
      //    WITHOUT the user pressing back (e.g. close button, parent state
      //    change), the entry is still in the stack. Pop it so the user
      //    doesn't need an extra back press to leave the page.
      //
      //    However, if something else (e.g. clearFilter / applyFilter) pushed
      //    a NEW history entry on top of ours while the modal was still open,
      //    our entry is now buried. Calling goBack() would pop that newer
      //    entry instead of ours, undoing the filter change. In that case we
      //    skip the goBack — the newer entry already has the correct URL.
      // -----------------------------------------------------------------------
      // Skip if the caller's own close path has already fired (or is about to
      // fire) a goBack() for this entry — otherwise we'd pop twice.
      const callerAlreadyWentBack = Boolean(didGoBackRef && didGoBackRef.current);

      if (didPush && !wasPopped && !callerAlreadyWentBack) {
        const currentState =
          typeof window !== "undefined" && window.history?.state
            ? unwrapState(window.history.state)
            : {};
        const currentModals = getModalsFromState(currentState);
        const isStillOnTop = currentModals[myIndex] === modalType;

        if (isStillOnTop && !isViewTransitionNavigating()) {
          if (didGoBackRef) {
            didGoBackRef.current = true;
          }
          history.goBack();
        }
        // else: our entry is buried under a newer push (or a view
        // transition navigation is in progress and will bury it) — skip goBack
      }
    };
  }, []);
};

export default useModal;
